import { useState, useEffect } from 'react';
import MetricsListTab from './MetricsListTab';
import { ExamlistTab } from './ExamListTab';
import QuestionListTab from '../Admin/Questions';
import QuestionBook from '../ProgramOfStudies/QuestionBook';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import MockQuestionBooks from '../ProgramOfStudies';
import { useQuestionBook } from '@/hooks/useQuestionBook';
import { DashboardContent } from '@/layouts/dashboard';

export function Exams(props) {
  const { selectedTab, uri } = props;

  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMocks, setLoadingMocks] = useState(true);
  const [mockQuestionBooks, setMockQuestionBooks] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const client = useClient();
  const { questionBooks, setQuestionBooks } = useQuestionBook();
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);

  const getCourses = async () => {
    try {
      const { data } = await client.fetch('courses/simple');
      setCourses(data);
    } catch {
      toast.error(t('toast.errorGetCourse'));
    }
  };

  const getExams = async () => {
    setLoading(true);
    try {
      const { data } = await client.fetch('question_books?filter[visibility][]=exam');
      setExams(
        data.map(item => {
          item.status = getStatus(item);
          return item;
        })
      );
    } catch {
      toast.error(t('toast.errorGetExams'));
    }
    setLoading(false);
  };

  const getMockQuestionBooks = async () => {
    setLoadingMocks(true);
    const { data, error } = await client.fetch('question_books?filter[visibility]=open');
    if (error) {
      toast.error(t('toast.errorGetSimulations'));
    } else {
      const newData = data?.map(item => {
        if (moment().isAfter(item?.['finished-at'])) {
          return { ...item, status: 'Realizado' };
        } else {
          return { ...item, status: 'Não realizado' };
        }
      });
      setMockQuestionBooks(newData);
    }
    setLoadingMocks(false);
  };

  const getQuestionBooks = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('question_books?filter[visibility]=train');
    if (error) {
      toast.error('Erro ao buscar apostilas');
    } else {
      setQuestionBooks(data.filter(q => q.visibility === 'train'));
    }
    setLoading(false);
  };

  const ExamsAndMocks = [...exams, ...mockQuestionBooks];

  const getClassrooms = async () => {
    setLoadingClassrooms(true);
    const { data, error } = await client.fetch('/classrooms?simple=true');
    if (error) {
      toast.error('Erro ao buscar turmas');
    } else {
      setClassrooms(data);
    }
    setLoadingClassrooms(false);
  };

  const fetchTaxonomies = async () => {
    try {
      const { data } = await client.fetch(`taxonomies?simple=true`);
      setTaxonomies(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    switch (selectedTab) {
      case 'provas':
        courses?.length === 0 && getCourses();
        exams?.length === 0 && getExams();
        classrooms?.length === 0 && getClassrooms();
        break;
      case 'simulados':
        courses?.length === 0 && getCourses();
        mockQuestionBooks?.length === 0 && getMockQuestionBooks();
        break;
      case 'resultados':
        courses?.length === 0 && getCourses();
        mockQuestionBooks?.length === 0 && getMockQuestionBooks();
        exams?.length === 0 && getExams();
        break;
      case 'apostilas':
        courses?.length === 0 && getCourses();
        classrooms?.length === 0 && getClassrooms();
        questionBooks?.length === 0 && getQuestionBooks();
        break;
      case 'banco-de-questoes':
        taxonomies?.length === 0 && fetchTaxonomies();
        break;
    }
  }, [selectedTab]);

  const handleDeleteExam = examId => {
    setExams(exams.filter(e => e.id !== examId));
  };

  const getStatus = exam => {
    const actualDate = moment();
    const initialTime = exam?.['published-at'];
    const finishTime = exam?.['finished-at'];

    if (!initialTime || !finishTime) {
      return 'Em andamento';
    } else if (actualDate.isBefore(initialTime)) {
      return 'Não realizada';
    } else if (actualDate.isBetween(initialTime, finishTime)) {
      return 'Em andamento';
    } else {
      return 'Realizada';
    }
  };

  const breadcrumbs = {
    title: 'Ferramentas de Avaliação',
    nav: [
      {
        route: uri,
        name: selectedTab === 'apostilas' ? 'Exercícios' : selectedTab === 'provas' ? 'Provas' : selectedTab === 'simulados' ? 'Simulados' : 'Banco de Questões',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="tab__content">
        {props.selectedTab === 'provas' && (
          <ExamlistTab
            handleDeleteExam={handleDeleteExam}
            courses={courses}
            classrooms={classrooms}
            loadingClassrooms={loadingClassrooms}
            exams={exams}
            loading={loading}
          />
        )}
        {props.selectedTab === 'resultados' && (
          <MetricsListTab
            loading={loading}
            exams={ExamsAndMocks}
            courses={courses}
          />
        )}

        {props.selectedTab === 'banco-de-questoes' && <QuestionListTab taxonomies={taxonomies} />}
        {props.selectedTab === 'apostilas' && (
          <QuestionBook
            loadingClassrooms={loadingClassrooms}
            classrooms={classrooms}
            courses={courses}
            questionBooks={questionBooks}
            loading={loading}
          />
        )}
        {props.selectedTab === 'simulados' && (
          <MockQuestionBooks
            mockQuestionBooks={mockQuestionBooks}
            loading={loadingMocks}
            courses={courses}
          />
        )}
      </div>
    </DashboardContent>
  );
}
